import { useEffect, useMemo, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithToolCalls, type UIMessage } from "ai";
import type { Session } from "@supabase/supabase-js";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { API_BASE } from "../lib/api";
import { supabase } from "../lib/supabase";
import { useSelection } from "../hooks/useSelection";
import { useDocumentThread } from "../hooks/useDocumentThread";
import {
  readSelection,
  readSlide,
  getPresentationInfo,
  getPresentationTheme,
  addSlide,
  deleteSlide,
  addTextBox,
  addShape,
  setShapeText,
  formatShape,
  deleteShape,
  selectSlide,
  runPowerPointScript,
  getPowerPointCapabilities,
  moveSlide,
  duplicateSlide,
  applySlideLayout,
  getSlideImage,
  exportSlides,
  importSlides,
  addLine,
  addTable,
  readTable,
  updateTableCell,
  groupShapes,
  ungroupShapes,
  selectShapes,
  arrangeShapes,
  setShapeZOrder,
  setShapeMetadata,
  setShapeHyperlink,
  addImage,
  setSlideBackground,
} from "../lib/office";
import { AgentPicker } from "./AgentPicker";
import { ModelPicker, type Provider } from "./ModelPicker";
import { SelectionChip } from "./SelectionChip";
import { ToolCallCard } from "./ToolCallCard";
import { QuickActions } from "./QuickActions";

export function Chat({ session }: { session: Session }) {
  const { chatId, setChatId } = useDocumentThread();
  const [agentId, setAgentId] = useState<string | null>(null);
  const [provider, setProvider] = useState<Provider>("anthropic");
  const [model, setModel] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [includeSelection, setIncludeSelection] = useState(true);
  const selection = useSelection();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Refs so the customFetch + body callbacks always see latest values without
  // needing to rebuild the transport (which would break mid-stream).
  const chatIdRef = useRef(chatId);
  const agentIdRef = useRef(agentId);
  const providerRef = useRef(provider);
  const modelRef = useRef(model);
  const tokenRef = useRef(session.access_token);
  useEffect(() => { chatIdRef.current = chatId; }, [chatId]);
  useEffect(() => { agentIdRef.current = agentId; }, [agentId]);
  useEffect(() => { providerRef.current = provider; }, [provider]);
  useEffect(() => { modelRef.current = model; }, [model]);
  useEffect(() => { tokenRef.current = session.access_token; }, [session.access_token]);

  // Built once. customFetch reads refs + captures chatId from response header so
  // the auto-resubmit (after tool result) goes to the same chat instead of
  // creating a new one each time.
  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${API_BASE}/api/chat`,
        // Stream-resume na taskpane-reload: de server kent de lopende stream
        // via chats.active_stream_id; useChat's eigen chat-id is een lokaal
        // gegenereerd id, dus bouw de URL met het échte server-chatId.
        prepareReconnectToStreamRequest: () => ({
          api: `${API_BASE}/api/chat/${chatIdRef.current}/stream`,
        }),
        fetch: async (url, init) => {
          const patched: RequestInit = {
            ...init,
            headers: {
              ...(init?.headers as Record<string, string> | undefined),
              Authorization: `Bearer ${tokenRef.current}`,
            },
          };
          // Alleen POSTs hebben een body — de resume-GET niet (een GET met
          // body laat fetch() een TypeError gooien).
          if (init?.body) {
            const body = JSON.parse(init.body as string);
            patched.body = JSON.stringify({
              ...body,
              chatId: chatIdRef.current,
              agentId: agentIdRef.current,
              provider: providerRef.current,
              // null/undefined → backend pakt zijn default model
              model: modelRef.current ?? undefined,
              source: "powerpoint-addin",
              // Zelfde kolom als de Excel add-in — het is gewoon "document id".
              excel_document_id: Office.context.document.url || null,
            });
          }
          const res = await fetch(url, patched);
          const returned = res.headers.get("X-Chat-Id");
          if (returned && returned !== chatIdRef.current) {
            chatIdRef.current = returned;
            setChatId(returned);
          }
          return res;
        },
      }),
    [setChatId],
  );

  const { messages, sendMessage, status, error, addToolResult, stop, setMessages, resumeStream } = useChat({
    transport,
    // After all tool-calls in the latest assistant message have results,
    // automatically POST back to /api/chat so the model can continue.
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    onToolCall: async ({ toolCall }) => {
      const name = toolCall.toolName;
      const args = (toolCall.input ?? {}) as Record<string, unknown>;
      const toolCallId = toolCall.toolCallId;
      try {
        let output: unknown;
        switch (name) {
          // Reading
          case "read_selection":         output = await readSelection(); break;
          case "read_slide":             output = await readSlide(args.slide as number); break;
          case "get_presentation_info":  output = await getPresentationInfo(); break;
          case "get_presentation_theme": output = await getPresentationTheme(); break;
          case "get_powerpoint_capabilities": output = getPowerPointCapabilities(); break;

          // Slides
          case "add_slide":    output = await addSlide(args as Parameters<typeof addSlide>[0]); break;
          case "delete_slide": output = await deleteSlide(args.slide as number); break;
          case "select_slide": output = await selectSlide(args.slide as number); break;
          case "move_slide": output = await moveSlide(args as Parameters<typeof moveSlide>[0]); break;
          case "duplicate_slide": output = await duplicateSlide(args as Parameters<typeof duplicateSlide>[0]); break;
          case "apply_slide_layout": output = await applySlideLayout(args as Parameters<typeof applySlideLayout>[0]); break;
          case "get_slide_image": output = await getSlideImage(args as Parameters<typeof getSlideImage>[0]); break;
          case "export_slides": output = await exportSlides(args as Parameters<typeof exportSlides>[0]); break;
          case "import_slides": output = await importSlides(args as Parameters<typeof importSlides>[0]); break;
          case "set_slide_background": output = await setSlideBackground(args as Parameters<typeof setSlideBackground>[0]); break;

          // Shapes
          case "add_text_box":   output = await addTextBox(args as Parameters<typeof addTextBox>[0]); break;
          case "add_shape":      output = await addShape(args as Parameters<typeof addShape>[0]); break;
          case "set_shape_text": output = await setShapeText(args as Parameters<typeof setShapeText>[0]); break;
          case "format_shape":   output = await formatShape(args as Parameters<typeof formatShape>[0]); break;
          case "delete_shape":   output = await deleteShape(args as Parameters<typeof deleteShape>[0]); break;
          case "add_line": output = await addLine(args as Parameters<typeof addLine>[0]); break;
          case "add_table": output = await addTable(args as Parameters<typeof addTable>[0]); break;
          case "read_table": output = await readTable(args as Parameters<typeof readTable>[0]); break;
          case "update_table_cell": output = await updateTableCell(args as Parameters<typeof updateTableCell>[0]); break;
          case "group_shapes": output = await groupShapes(args as Parameters<typeof groupShapes>[0]); break;
          case "ungroup_shapes": output = await ungroupShapes(args as Parameters<typeof ungroupShapes>[0]); break;
          case "select_shapes": output = await selectShapes(args as Parameters<typeof selectShapes>[0]); break;
          case "arrange_shapes": output = await arrangeShapes(args as Parameters<typeof arrangeShapes>[0]); break;
          case "set_shape_z_order": output = await setShapeZOrder(args as Parameters<typeof setShapeZOrder>[0]); break;
          case "set_shape_metadata": output = await setShapeMetadata(args as Parameters<typeof setShapeMetadata>[0]); break;
          case "set_shape_hyperlink": output = await setShapeHyperlink(args as Parameters<typeof setShapeHyperlink>[0]); break;
          case "add_image": output = await addImage(args as Parameters<typeof addImage>[0]); break;

          // Escape hatch
          case "run_powerpoint_script":
            output = await runPowerPointScript(args as Parameters<typeof runPowerPointScript>[0]);
            break;

          default:
            output = { error: `Unknown tool: ${name}` };
        }
        // Backstop: nooit megabytes de chatgeschiedenis in — dat sloopt de
        // model-context ("prompt is too long"). De reads cappen zelf al netjes.
        if (JSON.stringify(output ?? null).length > 200_000) {
          output = {
            error:
              "Resultaat te groot om terug te sturen. Lees één dia tegelijk of gebruik get_presentation_info voor een overzicht.",
          };
        }
        addToolResult({ tool: name, toolCallId, output });
      } catch (e) {
        addToolResult({
          tool: name,
          toolCallId,
          output: { error: e instanceof Error ? e.message : String(e) },
        });
      }
    },
  });

  // chatId capture happens in transport.fetch above (reads X-Chat-Id header
  // immediately after first response → auto-resubmit hits the same chat).

  // Herstel de gespreksgeschiedenis na een taskpane-reload en reattach aan
  // een eventueel nog lopende generatie. De chatId reist mee met de
  // presentatie (document settings); de berichten staan in de DB.
  const [loadingHistory, setLoadingHistory] = useState(false);
  const historyLoadedRef = useRef(false);
  useEffect(() => {
    if (!chatId || historyLoadedRef.current) return;
    historyLoadedRef.current = true;
    setLoadingHistory(true);
    fetch(`${API_BASE}/api/chat/messages?chatId=${encodeURIComponent(chatId)}`, {
      headers: { Authorization: `Bearer ${tokenRef.current}` },
    })
      .then((r) => (r.ok ? r.json() : { messages: [] }))
      .then((d: { messages?: UIMessage[] }) => {
        if (d.messages?.length) {
          setMessages((prev) => (prev.length ? prev : d.messages!));
        }
      })
      .catch(() => {}) // geen geschiedenis is geen ramp; chat werkt gewoon
      .finally(() => {
        setLoadingHistory(false);
        // Server antwoordt 204 als er niets loopt — no-op.
        resumeStream().catch(() => {});
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, setMessages]);

  // Stop moet óók de server bereiken: de stream wordt server-side
  // doorgeconsumeerd (voor resume), dus alleen de fetch afbreken laat de
  // generatie doorlopen.
  function hardStop() {
    stop();
    const id = chatIdRef.current;
    if (id) {
      fetch(`${API_BASE}/api/chat/${id}/stop`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tokenRef.current}` },
      }).catch(() => {});
    }
  }

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length]);

  async function send() {
    const text = input.trim();
    if (!text) return;
    const slideNumbers = selection?.slides.map((s) => s.slide).filter((n) => n > 0) ?? [];
    const prefix =
      includeSelection && slideNumbers.length
        ? `[Selectie: dia ${slideNumbers.join(", ")}${selection && selection.shapes.length ? `, ${selection.shapes.length} shape(s)` : ""}]\n\n`
        : "";
    setInput("");
    await sendMessage({ text: prefix + text });
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div className="flex min-w-0 flex-wrap items-center gap-1">
          <AgentPicker token={session.access_token} agentId={agentId} onChange={setAgentId} />
          <ModelPicker
            token={session.access_token}
            provider={provider}
            model={model}
            onChange={(p, m) => {
              setProvider(p);
              setModel(m);
            }}
          />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            onClick={() => {
              hardStop();
              setChatId(null);
              setMessages([]);
            }}
            className="text-xs text-muted-fg hover:text-fg"
            title="Start een nieuwe chat voor deze presentatie"
          >
            Nieuw
          </button>
          <button
            onClick={() => supabase.auth.signOut()}
            className="text-xs text-muted-fg hover:text-fg"
          >
            Uitloggen
          </button>
        </div>
      </header>

      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-3 py-3">
        {messages.length === 0 && (
          <div className="text-xs text-muted-fg">
            {loadingHistory
              ? "Gesprek laden…"
              : "Stel een vraag of selecteer een dia en vraag de AI om te schrijven, samen te vatten of dia's te bouwen."}
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className="space-y-1">
            <div className="text-[10px] font-medium uppercase tracking-wide text-muted-fg/70">
              {m.role === "user" ? "Jij" : "Assistent"}
            </div>
            <div className="text-sm leading-relaxed">
              {m.parts?.map((p, i) => {
                if (p.type === "text") {
                  return (
                    <div key={i} className="md">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{p.text}</ReactMarkdown>
                    </div>
                  );
                }
                if (p.type?.startsWith("tool-")) {
                  return <ToolCallCard key={i} part={p as Parameters<typeof ToolCallCard>[0]["part"]} />;
                }
                return null;
              })}
            </div>
          </div>
        ))}
        {status === "streaming" && (
          <div className="text-xs text-muted-fg/70">…</div>
        )}
        {error && (
          <div className="rounded border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
            <div className="font-medium">Fout</div>
            <div className="mt-0.5 whitespace-pre-wrap break-words">
              {error instanceof Error ? error.message : String(error)}
            </div>
            <div className="mt-1 text-[10px] text-destructive/70">
              Check de console (rechtsklik paneel → Inspect) voor details.
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-border p-2">
        <div className="mb-1.5">
          <QuickActions hasSelection={!!selection && selection.slides.length > 0} onPick={(p) => setInput(p)} />
        </div>
        <div className="mb-1 flex items-center gap-2">
          <SelectionChip
            selection={selection}
            enabled={includeSelection}
            onToggle={() => setIncludeSelection((v) => !v)}
          />
        </div>
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={2}
            placeholder="Vraag iets…"
            className="flex-1 resize-none rounded border border-border px-2 py-1.5 text-sm outline-none focus:border-primary"
          />
          {status === "streaming" || status === "submitted" ? (
            <button
              onClick={hardStop}
              className="rounded border border-border bg-bg px-3 py-1.5 text-sm text-fg hover:border-primary"
            >
              Stop
            </button>
          ) : (
            <button
              onClick={send}
              disabled={!input.trim()}
              className="rounded bg-primary px-3 py-1.5 text-sm text-primary-fg disabled:opacity-40"
            >
              Stuur
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
